import React, { useCallback, useEffect, useState } from 'react';
import recruitingTasksService, {
  RecruitingTask, TASK_PRIORITY_LABEL, TASK_TYPE_LABEL,
} from '../../../services/recruitingTasks.service';
import CandidateTaskForm from './CandidateTaskForm';
import { relativeZeit, istUeberfaellig } from './zeit';

/** Offene und erledigte Aufgaben eines Kandidaten. */

interface Props {
  candidateId: string;
  aktualisierungsZaehler?: number;
  onGeaendert?: () => void;
}

export const CandidateTaskList: React.FC<Props> = ({
  candidateId, aktualisierungsZaehler = 0, onGeaendert,
}) => {
  const [aufgaben, setAufgaben] = useState<RecruitingTask[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formularOffen, setFormularOffen] = useState(false);
  const [zeigeErledigte, setZeigeErledigte] = useState(false);

  const laden = useCallback(async () => {
    try {
      setLaedt(true);
      const liste = await recruitingTasksService.liste({ candidate_id: candidateId, limit: 50 });
      setAufgaben(liste.items);
      setFehler(null);
    } catch (e: any) {
      setFehler(e?.message || 'Die Aufgaben konnten nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [candidateId]);

  useEffect(() => { laden(); }, [laden, aktualisierungsZaehler]);

  const statusSetzen = async (aufgabe: RecruitingTask, status: 'DONE' | 'CANCELLED' | 'IN_PROGRESS') => {
    try {
      await recruitingTasksService.setzeStatus(aufgabe.id, status);
      await laden();
      onGeaendert?.();
    } catch (e: any) {
      setFehler(e?.message || 'Der Status konnte nicht geändert werden.');
    }
  };

  const offene = aufgaben.filter((a) => a.status === 'OPEN' || a.status === 'IN_PROGRESS');
  const erledigte = aufgaben.filter((a) => a.status === 'DONE' || a.status === 'CANCELLED');

  const zeile = (a: RecruitingTask) => {
    const offen = a.status === 'OPEN' || a.status === 'IN_PROGRESS';
    const ueberfaellig = offen && istUeberfaellig(a.due_at);
    const prio = TASK_PRIORITY_LABEL[a.priority];

    return (
      <div
        key={a.id}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '9px 10px',
          borderRadius: 'var(--radius-md)', background: 'var(--glass)',
          border: `1px solid ${ueberfaellig ? '#EF444455' : 'var(--glass-border)'}`,
          opacity: offen ? 1 : 0.6,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: '0.86rem', fontWeight: 600, color: 'var(--text-primary)',
              textDecoration: a.status === 'CANCELLED' ? 'line-through' : 'none',
            }}>
              {a.title}
            </span>
            {a.priority !== 'NORMAL' && (
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: prio.color }}>{prio.label}</span>
            )}
          </div>
          <div style={{ fontSize: '0.76rem', color: ueberfaellig ? '#EF4444' : 'var(--text-tertiary)', marginTop: '2px' }}>
            {TASK_TYPE_LABEL[a.task_type]}
            {a.due_at ? ` · ${ueberfaellig ? 'überfällig seit ' : 'fällig '}${relativeZeit(a.due_at)}` : ' · ohne Frist'}
          </div>
        </div>

        {offen && (
          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
            <button
              className="btn btn-ghost" style={{ fontSize: '0.74rem', padding: '3px 8px' }}
              onClick={() => statusSetzen(a, 'DONE')}
            >
              Erledigt
            </button>
            <button
              className="btn btn-ghost" style={{ fontSize: '0.74rem', padding: '3px 8px', color: 'var(--text-tertiary)' }}
              onClick={() => statusSetzen(a, 'CANCELLED')}
            >
              Storno
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>
          {offene.length} {offene.length === 1 ? 'offene Aufgabe' : 'offene Aufgaben'}
        </span>
        <button
          className="btn btn-ghost"
          style={{ marginLeft: 'auto', fontSize: '0.78rem', padding: '4px 10px' }}
          onClick={() => setFormularOffen(!formularOffen)}
        >
          {formularOffen ? 'Abbrechen' : '+ Aufgabe'}
        </button>
      </div>

      {formularOffen && (
        <div style={{ marginBottom: '12px' }}>
          <CandidateTaskForm
            candidateId={candidateId}
            onAbbrechen={() => setFormularOffen(false)}
            onAngelegt={() => { setFormularOffen(false); laden(); onGeaendert?.(); }}
          />
        </div>
      )}

      {fehler && <div style={{ color: '#EF4444', fontSize: '0.82rem', marginBottom: '10px' }}>{fehler}</div>}

      {laedt && aufgaben.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Aufgaben werden geladen…</div>
      ) : (
        <div style={{ display: 'grid', gap: '7px' }}>
          {offene.length === 0 && !formularOffen && (
            <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Keine offene Aufgabe.</div>
          )}
          {offene.map(zeile)}

          {erledigte.length > 0 && (
            <>
              <button
                className="btn btn-ghost"
                style={{ fontSize: '0.78rem', justifySelf: 'start', padding: '3px 0' }}
                onClick={() => setZeigeErledigte(!zeigeErledigte)}
              >
                {zeigeErledigte ? '▾' : '▸'} Abgeschlossen ({erledigte.length})
              </button>
              {zeigeErledigte && erledigte.map(zeile)}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CandidateTaskList;
